import React, { useState, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { MasterDocument, WorkItem, MasterDocumentSubId } from "./ResponsibilityAreaTable";

type MissingSubIdsTreeProps = {
  wi: WorkItem;
  md: MasterDocument;
  missingSubIds: MasterDocumentSubId[];
  checkedSubIds: string[];
  setCheckedSubIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export default function MissingSubIdsTree({ wi, md, missingSubIds, checkedSubIds, setCheckedSubIds }: MissingSubIdsTreeProps) {
  const [tree, setTree] = useState(null);

  useEffect(() => {
    if (!md?.ToCs) return;

    let tempChecked: string[] = [];

    const attachParents = (node, parent = null) => {
      node.__parent = parent;

      if (!node.Id) node.Id = node.DocumentId;
      node.IsAssignable = false;
      if (missingSubIds.some((subId) => subId.SubId === node.Id) || wi.SubIds?.some((subId) => subId.SubId === node.Id)) {
        node.IsAssignable = true;
        makeAllParentsAssignable(node);
      }
      // Only add leaf nodes to checked state
      if ((!node.Children || node.Children.length === 0) && wi.SubIds?.some((subId) => subId.SubId === node.Id)) {
        tempChecked.push(node.Id || node.DocumentId);
      }

      node.Children?.forEach((child) => attachParents(child, node));
      return node;
    };

    const clonedTree = structuredClone(md.ToCs);
    const processedTree = attachParents(clonedTree);

    setCheckedSubIds(tempChecked);
    setTree(processedTree);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [md, wi, missingSubIds]);

  const handleCheck = (node, isChecked) => {
    setCheckedSubIds((prev: string[]) => {
      const updated = new Set(prev); // Use a Set to automatically prevent duplicates

      const setDescendants = (n, state) => {
        const id = n.Id || n.DocumentId;

        // Only act on leaf nodes
        if (!n.Children || n.Children.length === 0) {
          if (state) {
            updated.add(id); // Add to set (duplicates automatically ignored)
          } else {
            updated.delete(id); // Remove from set
          }
        }

        n.Children?.forEach((child) => setDescendants(child, state));
      };

      setDescendants(node, isChecked);

      return Array.from(updated); // Convert Set back to array for state
    });
  };

  return (
    <div className="p-4">
      {tree && <TreeNode node={tree} checkedSubIds={checkedSubIds} onCheck={handleCheck} defaultExpanded />}
    </div>
  );
}

function TreeNode({ node, checkedSubIds, onCheck, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!node) return null;
  if (!node.IsAssignable) return null;

  const toggleExpand = () => setExpanded((prev) => !prev);

  // Parent checkbox is considered checked if all children are checked
  const isParent = node.Children && node.Children.length > 0;
  const allChildrenChecked =
    isParent &&
    node.Children.every((child) =>
      !child.Children || child.Children.length === 0
        ? checkedSubIds.includes(child.Id || child.DocumentId)
        : areAllDescendantsChecked(child, checkedSubIds)
    );
  const someChildrenChecked =
    isParent &&
    node.Children.some((child) =>
      !child.Children || child.Children.length === 0
        ? checkedSubIds.includes(child.Id || child.DocumentId)
        : areSomeDescendantsChecked(child, checkedSubIds)
    );

  return (
    <div className="ml-4" key={node.Id || node.DocumentId}>
      <div className="flex items-center">
        {isParent && (
          <button onClick={toggleExpand} className="w-4 mr-1 text-gray-500 focus:outline-none">
            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </button>
        )}
        <div className="relative">
          <input
            type="checkbox"
            checked={isParent ? allChildrenChecked : !!checkedSubIds.includes(node.Id || node.DocumentId)}
            ref={(el) => {
              if (el) el.indeterminate = isParent && !allChildrenChecked && someChildrenChecked;
            }}
            onChange={(e) => onCheck(node, e.target.checked)}
            className="mr-2 w-4 h-4 border rounded"
          />
        </div>
        <span>{node.Title}</span>
      </div>

      {expanded && isParent && (
        <div className="ml-6 mt-1 border-l border-gray-300 pl-2">
          {node.Children.map((child) => (
            <TreeNode
              key={child.Id || child.DocumentId}
              node={child}
              checkedSubIds={checkedSubIds}
              onCheck={onCheck}
              defaultExpanded={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}

//  Helper functions to check descendants’ state
function areAllDescendantsChecked(node, checkedSubIds) {
  if (!node.Children || node.Children.length === 0) {
    return !!checkedSubIds.includes(node.Id || node.DocumentId);
  }
  return node.Children.every((child) => areAllDescendantsChecked(child, checkedSubIds));
}

function areSomeDescendantsChecked(node, checkedSubIds) {
  if (!node.Children || node.Children.length === 0) {
    return !!checkedSubIds.includes(node.Id || node.DocumentId);
  }
  return node.Children.some((child) => areSomeDescendantsChecked(child, checkedSubIds));
}

function makeAllParentsAssignable(node) {
  if (node.__parent && !node.__parent.IsAssignable) {
    node.__parent.IsAssignable = true;
    makeAllParentsAssignable(node.__parent);
  }
}
